/**
 * Signal confirmation: testable checks on the candle a UT Bot signal fired
 * on, so each signal carries a CONFIRM / MIXED / REJECT verdict. Shadow mode
 * for now: the verdict is shown and stored, it doesn't block auto-trade.
 * Indicators come from trading-signals (streamed once per candle series).
 */

import { ADX, EMA, RSI, SMA } from 'trading-signals';
import type { Candle, SignalConfirmation, ConfirmationCheck, UTBotSignal } from '../types/index.js';

const TREND_EMA    = 200;
const ADX_PERIOD   = 14;
const ADX_TRENDING = 20;
const RSI_PERIOD   = 14;
const VOLUME_SMA   = 20;

type Series = { ema: (number | null)[]; adx: (number | null)[]; upTrend: (boolean | null)[]; rsi: (number | null)[]; volSma: (number | null)[] };

function series(candles: Candle[]): Series {
  const ema = new EMA(TREND_EMA), adx = new ADX(ADX_PERIOD), rsi = new RSI(RSI_PERIOD), vol = new SMA(VOLUME_SMA);
  const out: Series = { ema: [], adx: [], upTrend: [], rsi: [], volSma: [] };
  for (const c of candles) {
    // Each value is what was known at that candle's close (volume SMA excludes the candle itself).
    out.volSma.push(vol.isStable ? vol.getResult() : null);
    vol.add(c.volume);
    ema.add(c.close);
    adx.add({ high: c.high, low: c.low, close: c.close });
    rsi.add(c.close);
    // Only once an indicator has its full lookback (some return early values).
    out.ema.push(ema.isStable ? ema.getResult() : null);
    out.adx.push(adx.isStable ? adx.getResult() : null);
    // +DI above -DI: the trend ADX measures is pointing up
    const pdi = adx.pdi, mdi = adx.mdi;
    out.upTrend.push(adx.isStable && typeof pdi === 'number' && typeof mdi === 'number' ? pdi > mdi : null);
    out.rsi.push(rsi.isStable ? rsi.getResult() : null);
  }
  return out;
}

function checksAt(s: Series, candles: Candle[], i: number, type: UTBotSignal['type']): ConfirmationCheck[] {
  const c   = candles[i]!;
  const buy = type === 'BUY';
  const ema = s.ema[i] ?? null, adx = s.adx[i] ?? null, up = s.upTrend[i] ?? null, rsi = s.rsi[i] ?? null, volSma = s.volSma[i] ?? null;
  const hasVolume = c.volume > 0 && volSma !== null && volSma > 0;
  return [
    { name: 'TREND', pass: ema === null ? null : buy ? c.close > ema : c.close < ema,
      detail: ema === null ? `EMA ${TREND_EMA}: not enough history`
        : `close ${c.close > ema ? 'above' : 'below'} EMA ${TREND_EMA} (${ema.toPrecision(6)}); ${buy ? 'BUY' : 'SELL'} wants ${buy ? 'above' : 'below'}` },
    // A strong trend only supports the signal if it points the same way.
    { name: 'STRENGTH', pass: adx === null || up === null ? null : adx >= ADX_TRENDING && up === buy,
      detail: adx === null || up === null ? 'ADX: not enough history'
        : `ADX ${adx.toFixed(1)} (trending ≥ ${ADX_TRENDING}), trend ${up ? 'up' : 'down'}` },
    { name: 'VOLUME', pass: hasVolume ? c.volume >= volSma : null,
      detail: hasVolume ? `volume ${(c.volume / volSma).toFixed(2)}× its ${VOLUME_SMA}-candle average` : 'no volume data' },
    { name: 'MOMENTUM', pass: rsi === null ? null : buy ? rsi < 70 : rsi > 30,
      detail: rsi === null ? 'RSI: not enough history' : `RSI ${rsi.toFixed(1)} (${buy ? 'not overbought < 70' : 'not oversold > 30'})` },
  ];
}

function verdictOf(checks: ConfirmationCheck[]): Pick<SignalConfirmation, 'passed' | 'total' | 'verdict'> {
  const known  = checks.filter(c => c.pass !== null);
  const passed = known.filter(c => c.pass).length;
  const total  = known.length;
  // Failing both trend checks = trading against an established trend.
  const againstTrend = checks.find(c => c.name === 'TREND')?.pass === false && checks.find(c => c.name === 'STRENGTH')?.pass === false;
  const verdict = total === 0 ? 'MIXED'
    : passed === total ? 'CONFIRM'
    : againstTrend || passed * 2 < total ? 'REJECT'
    : 'MIXED';
  return { passed, total, verdict };
}

/** Attach a confirmation to each signal. `candles` must be the series the signals were computed on. */
export function withConfirmations(candles: Candle[], signals: UTBotSignal[]): UTBotSignal[] {
  if (signals.length === 0) return signals;
  const s = series(candles);
  return signals.map(sig => {
    const checks = checksAt(s, candles, sig.candleIndex, sig.type);
    return { ...sig, confirmation: { checks, ...verdictOf(checks), ai: null } };
  });
}
